/**
 * Vendor Bot Shop - Customer Wallet Page
 * Customer wallet balance, transactions, add funds
 */

import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { BottomNavigation } from '../../components/customer';
import { useTranslation } from '../../hooks/useTranslation';

interface WalletData {
  balance: number;
  points_balance: number;
  currency_symbol: string;
  currency_code: string;
  referral_code: string;
  referral_count: number;
  total_orders: number;
  total_spent: number;
}

interface Transaction {
  id: number;
  transaction_type: string;
  amount: number;
  description: string;
  created_at: string;
  reference_id?: string;
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--tg-theme-bg-color, #1C1C1E);
  padding-bottom: calc(70px + env(safe-area-inset-bottom, 0px));
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 375px) {
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  }

  @media (min-width: 768px) {
    max-width: 900px;
  }
`;

const Header = styled.div`
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid var(--tg-theme-hint-color, #3a3a3c);

  @media (min-width: 375px) {
    padding: 16px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #fff);
  margin: 0;

  @media (min-width: 375px) {
    font-size: 20px;
  }
`;

const BalanceCard = styled.div`
  margin: 12px;
  padding: 16px;
  background: linear-gradient(135deg, var(--tg-theme-button-color, #4CAF50) 0%, #45a049 100%);
  border-radius: 12px;
  text-align: center;
  color: #fff;

  @media (min-width: 375px) {
    margin: 16px;
    padding: 24px;
    border-radius: 16px;
  }
`;

const BalanceLabel = styled.div`
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
`;

const BalanceAmount = styled.div`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;

  @media (min-width: 375px) {
    font-size: 36px;
    margin-bottom: 16px;
  }
`;

const PointsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  font-size: 14px;
  opacity: 0.9;
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.2s;

  &:active {
    background: rgba(255, 255, 255, 0.3);
  }

  @media (min-width: 375px) {
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 14px;
    gap: 8px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 12px;

  @media (min-width: 375px) {
    gap: 12px;
    margin: 16px;
  }
`;

const StatCard = styled.div`
  background: var(--tg-theme-secondary-bg-color, #2C2C2E);
  border-radius: 10px;
  padding: 12px 8px;
  text-align: center;

  @media (min-width: 375px) {
    border-radius: 12px;
    padding: 16px;
  }
`;

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #fff);
  margin-bottom: 4px;

  @media (min-width: 375px) {
    font-size: 20px;
  }
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #8e8e93);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  padding-bottom: 8px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #fff);
  margin: 0;
`;

const TransactionList = styled.div`
  padding: 0 12px;

  @media (min-width: 375px) {
    padding: 0 16px;
  }
`;

const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--tg-theme-secondary-bg-color, #2C2C2E);
  border-radius: 10px;
  margin-bottom: 6px;

  @media (min-width: 375px) {
    padding: 16px;
    border-radius: 12px;
    margin-bottom: 8px;
  }
`;

const TransactionInfo = styled.div`
  flex: 1;
`;

const TransactionType = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #fff);
  margin-bottom: 4px;
`;

const TransactionDate = styled.div`
  font-size: 13px;
  color: var(--tg-theme-hint-color, #8e8e93);
`;

const TransactionAmount = styled.div<{ $positive: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ $positive }) => $positive ? '#4CAF50' : '#FF5252'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--tg-theme-hint-color, #8e8e93);
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--tg-theme-hint-color, #8e8e93);
`;

const ShopWallet: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const { t } = useTranslation();
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  const loadWalletData = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch wallet data
      const walletRes = await fetch(`${baseUrl}/api/wallet/${botUsername}/balance`, { headers });
      const walletJson = await walletRes.json();

      if (walletJson.success) {
        setWalletData(walletJson.data);
      }

      // Fetch transactions
      const txRes = await fetch(`${baseUrl}/api/wallet/${botUsername}/transactions?limit=20`, { headers });
      const txJson = await txRes.json();

      if (txJson.success) {
        setTransactions(txJson.data || []);
      }
    } catch (error) {
      console.error('Error loading wallet data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [botUsername, baseUrl]);

  useEffect(() => {
    loadWalletData();
  }, [loadWalletData]);

  const handleAddFunds = () => {
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium');
    // TODO: Navigate to add funds page or show modal
    window.Telegram?.WebApp?.showAlert(t('wallet.addFundsComingSoon'));
  };

  const formatTransactionType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      'deposit': `💰 ${t('wallet.transactionTypes.deposit')}`,
      'withdrawal': `💸 ${t('wallet.transactionTypes.withdrawal')}`,
      'purchase': `🛒 ${t('wallet.transactionTypes.purchase')}`,
      'refund': `↩️ ${t('wallet.transactionTypes.refund')}`,
      'referral_bonus': `👥 ${t('wallet.transactionTypes.referralBonus')}`,
      'subscription_renewal': `🔄 ${t('wallet.transactionTypes.subscription')}`,
      'points_conversion': `🎁 ${t('wallet.transactionTypes.pointsConversion')}`
    };
    return typeMap[type] || type;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Header>
          <HeaderTitle>{t('wallet.wallet')}</HeaderTitle>
        </Header>
        <LoadingState>{t('wallet.loadingWallet')}</LoadingState>
        <BottomNavigation />
      </PageContainer>
    );
  }

  const currencySymbol = walletData?.currency_symbol || '$';

  return (
    <PageContainer>
      <Header>
        <HeaderTitle>{t('wallet.wallet')}</HeaderTitle>
      </Header>

      <BalanceCard>
        <BalanceLabel>{t('wallet.availableBalance')}</BalanceLabel>
        <BalanceAmount>
          {currencySymbol}{parseFloat(String(walletData?.balance || 0)).toFixed(2)}
        </BalanceAmount>
        <PointsRow>
          <span>🎁 {(walletData?.points_balance || 0).toLocaleString()} {t('profile.points').toLowerCase()}</span>
        </PointsRow>
        {/* Hidden for now - will be implemented later
        <ActionButtons>
          <ActionButton onClick={handleAddFunds}>
            💳 {t('wallet.addFunds')}
          </ActionButton>
          <ActionButton onClick={() => window.Telegram?.WebApp?.showAlert(t('wallet.withdrawComingSoon'))}>
            💸 {t('wallet.withdraw')}
          </ActionButton>
        </ActionButtons>
        */}
      </BalanceCard>

      <StatsGrid>
        <StatCard>
          <StatValue>{walletData?.total_orders || 0}</StatValue>
          <StatLabel>{t('profile.orders')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{currencySymbol}{parseFloat(String(walletData?.total_spent || 0)).toFixed(0)}</StatValue>
          <StatLabel>{t('profile.totalSpent')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{walletData?.referral_count || 0}</StatValue>
          <StatLabel>{t('wallet.referrals')}</StatLabel>
        </StatCard>
      </StatsGrid>

      <SectionHeader>
        <SectionTitle>{t('wallet.recentTransactions')}</SectionTitle>
      </SectionHeader>

      <TransactionList>
        {transactions.length === 0 ? (
          <EmptyState>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💰</div>
            <div>{t('wallet.noTransactions')}</div>
          </EmptyState>
        ) : (
          transactions.map((tx) => (
            <TransactionItem key={tx.id}>
              <TransactionInfo>
                <TransactionType>{formatTransactionType(tx.transaction_type)}</TransactionType>
                <TransactionDate>
                  {tx.description || formatDate(tx.created_at)}
                </TransactionDate>
              </TransactionInfo>
              <TransactionAmount $positive={parseFloat(String(tx.amount)) > 0}>
                {parseFloat(String(tx.amount)) > 0 ? '+' : ''}{currencySymbol}{Math.abs(parseFloat(String(tx.amount))).toFixed(2)}
              </TransactionAmount>
            </TransactionItem>
          ))
        )}
      </TransactionList>

      <BottomNavigation />
    </PageContainer>
  );
};

export default ShopWallet;
